import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { useRouter } from 'next/router';
import { parse } from 'date-fns/parse';
import { isValid } from 'date-fns/isValid';
import { isPast } from 'date-fns/isPast';
import { t } from '@lingui/macro';
import { Trans } from '@lingui/react';
import { Box, Container } from '@components/layout/Grid';
import largeTooltip from '@components/LargeTooltip';
import {
  BlogPostFieldsFragment,
  BundleAccessStrategyKind,
  BundleCategoryKind,
  BundleDocument,
  BundleQuery,
  BundleSectionKind,
  Document,
  EssentialGenericFieldsFragment,
  LiveEvent,
  Playlist,
  ProgramCategory,
  useBundleQuery,
  Video,
} from '@gql/generated';
import MetaTags from '@components/MetaTags';
import { GenericCardsSection } from '@ecosystems/GenericCardsSection';
import { DocumentCard } from '@organisms/cards/DocumentCard';
import { InfoPanel1Section } from '@ecosystems/bundle/InfoPanel1Section';
import { snackbarVar } from '@lib/apollo/policies';
import { SingleProgramCard } from '@ecosystems/bundle/SingleProgramCard';
import { BundleHeroSection } from '@ecosystems/bundle/HeroSection';
import { findParentElement } from '@lib/utils';
import { InfoPanel2Section } from '@ecosystems/bundle/InfoPanel2Section';
import { Logo, Wrapper } from '@ecosystems/navigations/header/subcomponents';
import PricingSection from '@ecosystems/pricing/PricingSection';
import { UserContext } from '@lib/contexts/UserProvider';
import Icon from '@components/Icon';
import VideoPlayer from '@organisms/video-player';
import BundleCard from '@organisms/cards/BundleCard';
import Spinner from '@components/Spinner';
import { BundleExtraSection } from '@ecosystems/bundle/BundleExtraSection';
import { PreviewSection } from '@ecosystems/bundle/PreviewSection';
import { PromotionSection } from '@ecosystems/bundle/PromotionSection';
import { LiveBannerSection } from '@ecosystems/bundle/LiveBannerSection';
import { TextSection } from '@ecosystems/bundle/2ColumnText';
import { InfoPanel3Section } from '@ecosystems/bundle/InfoPanel3Section';
import { ProgramCard } from '@ecosystems/search_page/cardsV2/ProgramCard';
import { LiveEventCard } from '@ecosystems/search_page/cardsV2/LiveEventCard';
import SearchPlaylistCard from '@ecosystems/search_page/cardsV2/PlaylistCard';
import { BlogCard } from '@ecosystems/search_page/cardsV2/BlogCard';
import SearchVideoCard from '@ecosystems/search_page/cardsV2/VideoCard';
import { buildProgramStructor } from '@pages/[lang]/videos/overview';
import AnalyticsManager from '@lib/analytics/manager';

export const GlobalStyle = createGlobalStyle<{ hideNav: boolean }>`
  #__next {
    overflow-x: unset;
  }
  
  ${({ hideNav }) =>
    hideNav
      ? `
        ${Wrapper} > *:not(${Logo}){ display: none; }
        ${Logo}{ margin: 0 auto; }
      `
      : ''}
`;

const Section = styled(GenericCardsSection)`
  display: flex;
  flex-direction: column;
  padding: 0 20px;

  &:not(:last-of-type) {
    padding-bottom: 0;
    margin-bottom: 10px;
  }
`;

const CardLink = styled.a`
  display: flex;
  text-decoration: none;

  :not([href]) {
    cursor: not-allowed;
  }
`;

const Loading = styled.img`
  display: block;
  width: 50px;
  height: 100px;
  margin: 0 auto;
`;

const BlogPostWrapper = styled.div`
  border-radius: 4px;
  overflow: hidden;
  transition: transform 0.15s ease-in-out;

  &:hover {
    transform: scale(1.02);
  }

  a {
    height: 100%;
    display: block;

    div:first-child {
      height: 100%;
    }
  }
`;

const Content = styled.div<{ sections: BundleSectionKind[] }>`
  ${({ sections }) => {
    return sections
      ?.map(
        (section, index) => `
          .${section} {
            order: ${index}; 
            ${
              index === 0 && BundleSectionKind['InfoPanel'] === section
                ? 'margin-top: 0;'
                : ''
            }
          }`
      )
      .join(' ');
  }}

  .ExtraSection {
    order: 10;
  }

  .Pricing {
    order: 100;
  }
`;

const LargeTooltip = styled(largeTooltip)`
  top: 150% !important;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
`;

const SectionContainer = styled(Box)<{ waitListEnabled?: boolean }>`
  h2 {
    font-size: 24px;
  }

  ${({ waitListEnabled }) =>
    waitListEnabled
      ? `
        a[href] {
          pointer-events: none;
        }
      `
      : ''}
`;

const TrailerWrapper = styled.div`
  flex: 1;
`;

const TrailerVideo = styled(VideoPlayer)`
  width: 100%;

  @media (min-width: 860px) {
    width: 620px;
  }

  .video-js.vjs-default-skin[data-vjs-player] {
    height: 100%;

    .videoplayer-tag {
      background-color: #119edd;
    }

    .vjs-poster {
      background-size: cover;
    }
  }
`;

const BundleInfoTooltip = (props: {
  clientLogo: string;
  clientName: string;
  clientInfo: string;
  paidBundle: boolean;
  hasAccess: boolean;
}) => {
  const { clientLogo, clientName, clientInfo, paidBundle, hasAccess } = props;

  let tooltip = null;
  if (paidBundle) {
    tooltip = (
      <span className="clientText">
        {clientInfo ?? (
          <Trans
            id="bundle.id_page.hero.paid_client_text"
            values={{ clientName: clientName }}
          />
        )}
        <LargeTooltip>
          {t({ id: 'bundle.id_page.hero.paid_tooltip_text' })}
        </LargeTooltip>
      </span>
    );
  } else {
    tooltip = (
      <span className="clientText">
        {clientInfo ?? (
          <Trans
            id="bundle.id_page.hero.client_text"
            values={{ clientName: clientName }}
          />
        )}
        <LargeTooltip>
          {t({ id: 'bundle.id_page.hero.tooltip_text' })}
        </LargeTooltip>
      </span>
    );
  }

  return (
    <>
      {clientLogo ? (
        <span className="flex items-center gap-2 logos">
          <Icon name="ygb-icon-yogobe-logo2019_1" />
          <span className="separator">X</span>
          <img className="h-4 object-contain" src={clientLogo} />
        </span>
      ) : null}
      {!hasAccess ? tooltip : null}
    </>
  );
};

export enum BundleAccessState {
  Active,
  Expired,
  Guest,
}

const BundlePage = (props) => {
  const router = useRouter();
  const slug = router.query.slug as string;
  const client = router.query.client as string;
  const lang = router.query.lang as string;
  const [currentUser] = useContext(UserContext);
  const containerRef = useRef<HTMLDivElement>(null);

  const initialLimit = 3;

  const { data, loading, refetch } = useBundleQuery({
    variables: {
      slug,
      client,
      previewParameter: (router?.query?.preview || '') as string,
    },
    ssr: false,
  });

  const bundle = (data?.bundle ||
    props.pageProps?.ssrData?.bundle) as BundleQuery['bundle'];

  if (!bundle?.id) {
    if (!loading) {
      return 'NOT FOUND';
    } else {
      return (
        <div>
          <Spinner />
        </div>
      );
    }
  }

  const isContentHubBundle =
    bundle?.bundleCategory === BundleCategoryKind['ContentHub'];

  const startDate = bundle?.waitingItemInfo?.startDate
    ? parse(bundle?.waitingItemInfo?.startDate, 'yyyy-MM-dd', new Date())
    : null;

  const isActiveWaitlist =
    bundle?.waitingItemInfo?.waitingListEnabled &&
    startDate &&
    !isPast(startDate);

  const userAccessState = useMemo<BundleAccessState>(() => {
    let access = BundleAccessState.Guest;
    const accessUntil = new Date(bundle?.userAccess?.accessUntil);
    if (isValid(accessUntil)) {
      if (!isPast(accessUntil)) {
        access = BundleAccessState.Active;
      } else {
        access = BundleAccessState.Expired;
      }
    }

    return access;
  }, [bundle?.userAccess?.accessUntil]);

  const getStartedUrl = useMemo(() => {
    const query = {};
    if (router.query.code) {
      query['code'] = router.query.code;
    }
    if (router.query.registrationCode) {
      query['registrationCode'] = router.query.registrationCode;
    }

    return {
      pathname: `/${lang}/c/${client}/b/${slug}/get-started`,
      query,
    };
  }, [router?.query?.code]);

  const anchorClicksHandlers = useCallback(
    (e) => {
      const clickedInContentSection = findParentElement(
        e.target,
        '.Content__Section'
      );
      if (
        !clickedInContentSection ||
        (!isActiveWaitlist &&
          (userAccessState === BundleAccessState.Active || isContentHubBundle))
      ) {
        return null;
      }
      let anchor = e.target as HTMLElement;
      if (!(anchor instanceof HTMLAnchorElement)) {
        anchor = findParentElement(anchor, 'a');
      }
      if (anchor instanceof HTMLAnchorElement) {
        if (
          anchor.href.match(
            /\/(en|se|fi|no)\/(live|videos|programs|courses|challenges|playlists|blog)\/*/
          )
        ) {
          e.stopPropagation();
          e.preventDefault();
          router.push(getStartedUrl);
        }
      }
    },
    [userAccessState]
  );

  useEffect(() => {
    if (
      bundle?.id &&
      bundle?.accessStrategy === BundleAccessStrategyKind['WithPrice']
    ) {
      const new_customer =
        (currentUser?.extraPayments?.length || 0) +
          (currentUser?.receipts?.length || 0) ===
        0;
      AnalyticsManager().sendItemViewEvent({
        category: 'Bundle',
        slug: bundle?.id,
        title: bundle?.title,
        price: Math.floor(bundle?.price.originalPrice / 100),
        currency: bundle?.price.currency,
        new_customer,
        logged_in: !!currentUser?.id,
      });
    }
  }, [data?.bundle.id]);

  useEffect(() => {
    containerRef.current?.addEventListener('click', anchorClicksHandlers, true);

    return () => {
      containerRef.current?.removeEventListener(
        'click',
        anchorClicksHandlers,
        true
      );
    };
  }, [bundle?.userAccess]);

  const handleDocumentClick = useCallback(() => {
    //
    if (userAccessState !== BundleAccessState.Active) {
      snackbarVar({
        isSnackBarOpen: true,
        snackBarMessage: t({ id: 'bundle.id_page.documents.required_access' }),
      });
    }
  }, [userAccessState]);

  return (
    <div ref={containerRef}>
      <GlobalStyle hideNav={router.query?.source === 'external'} />
      <MetaTags
        title={bundle?.title}
        description={bundle?.metaDescription}
        thumbnailUrl={bundle?.metaImage}
      />
      {router?.query.hasOwnProperty('preview') && bundle.preview ? (
        <PreviewSection preview={bundle.preview} />
      ) : null}
      <BundleHeroSection
        bundle={bundle}
        {...(bundle?.trailer
          ? {
              video: (
                <TrailerWrapper>
                  <TrailerVideo
                    {...{
                      thumbnailImage: bundle.trailer?.thumbnailUrl,
                      video: bundle.trailer as Video,
                      elementID: 'bundle',
                      videoSources: bundle.trailer?.player?.assets,
                      isLimited: false,
                      autoPlay: false,
                    }}
                  />
                </TrailerWrapper>
              ),
            }
          : {
              image: bundle?.heroImage
                ? bundle?.heroImage
                : bundle?.footerCta?.image,
            })}
        title={bundle.title}
        titleAs="h1"
        subtitle={bundle.shortDescription}
        category={
          !bundle?.hideInfoText ? (
            <BundleInfoTooltip
              clientName={bundle?.client?.name}
              clientInfo={bundle?.client?.info}
              clientLogo={bundle?.client?.logo}
              paidBundle={
                bundle?.accessStrategy === BundleAccessStrategyKind['WithPrice']
              }
              hasAccess={bundle?.userAccess?.active}
            />
          ) : null
        }
        getStartedUrl={getStartedUrl}
        userAccessState={userAccessState}
      />
      <Content className="flex flex-col" sections={bundle?.sections}>
        {bundle?.bundleItems.sectionedVideos?.length > 0
          ? bundle?.bundleItems.sectionedVideos.map((section, i) => {
              return (
                <SectionContainer
                  key={i}
                  className={`Content__Section video_section_${i + 1}`}
                  waitListEnabled={isActiveWaitlist}
                  fullWidth
                  py={40}
                  px={20}
                >
                  <Container>
                    <Section
                      className="!p-0"
                      titleAs="h2"
                      title={
                        section.headline || (
                          <Trans id="bundle.id_page.videos_sections.title" />
                        )
                      }
                      viewAllUrl={isContentHubBundle && section.viewAllUrl}
                      items={section.items}
                      initialItemsLimit={initialLimit}
                      renderItem={(item: Video) => {
                        return (
                          <SearchVideoCard
                            key={item.identifier}
                            video={{
                              id: item.id,
                              identifier: item.identifier,
                              title: item.content.title,
                              thumbnailUrl: item.assets.thumbnailUrl,
                              instructorName:
                                item.taxonomies.instructors?.[0]?.name,
                              instructorSlug:
                                item.taxonomies.instructors?.[0]?.slug,
                              longDescription: item.content.longDescription,
                              kind: item.kind,
                              minutes: String(item.meta.minutes),
                              primaryStyleName: item.primaryStyle?.name,
                              primaryStyleSlug: item.primaryStyle?.slug,
                              shortDescription: item.content.shortDescription,
                            }}
                          />
                        );
                      }}
                    />
                  </Container>
                </SectionContainer>
              );
            })
          : null}
        {bundle?.bundleItems.playlists?.items?.length > 0 ? (
          <SectionContainer
            waitListEnabled={isActiveWaitlist}
            className={[
              'Content__Section ',
              BundleSectionKind['Playlists'],
            ].join(' ')}
            fullWidth
            py={40}
            px={20}
          >
            <Section
              className="!p-0"
              titleAs="h2"
              title={
                bundle?.bundleItems?.playlists?.headline || (
                  <Trans id="bundle.id_page.playlists_sections.title" />
                )
              }
              viewAllUrl={
                isContentHubBundle && bundle?.bundleItems?.playlists?.viewAllUrl
              }
              items={bundle?.bundleItems.playlists?.items}
              initialItemsLimit={initialLimit}
              renderItem={(item: Playlist) => {
                return (
                  <SearchPlaylistCard
                    key={item.id}
                    playlist={{
                      identifier: item.identifier,
                      id: item.id,
                      title: item.title,
                      description: item.description,
                      thumbnailUrl: item.assets.thumbnailUrl,
                      minutes: String(item.meta.minutes),
                      videoCount: String(item.meta.videoCount),
                      ownerId: item.owner.id,
                      visibility: item.visibility,
                    }}
                  />
                );
              }}
            />
          </SectionContainer>
        ) : null}
        {bundle?.bundleItems.liveEvents?.items?.length > 0 ? (
          <SectionContainer
            waitListEnabled={isActiveWaitlist}
            className={[
              'Content__Section ',
              BundleSectionKind['LiveEvents'],
            ].join(' ')}
            py={40}
            px={20}
            fullWidth
          >
            <Section
              className="!p-0"
              titleAs="h2"
              title={
                bundle?.bundleItems?.liveEvents?.headline || (
                  <Trans id="bundle.id_page.live_sections.title" />
                )
              }
              viewAllUrl={
                isContentHubBundle &&
                bundle?.bundleItems?.liveEvents?.viewAllUrl
              }
              items={bundle?.bundleItems.liveEvents?.items}
              initialItemsLimit={initialLimit}
              renderItem={(item: LiveEvent) => {
                return (
                  <LiveEventCard
                    key={item.id}
                    event={{
                      id: item.id,
                      kind: item.kind,
                      slug: item.slug,
                      theme: item.theme,
                      themeLabel: item.themeLabel,
                      thumbnailUrl: item.thumbnail,
                      title: item.title,
                      availableSpots: item.availableSpots,
                      bookingAvailable: item.bookingAvailable,
                      duration: item.duration,
                      eventDates: item.eventDates,
                      instructorName: item.primaryInstructor?.name,
                      instructorSlug: item.primaryInstructor?.slug,
                      maximumParticipants: item.maximumParticipants,
                      registrationEndDate: item.registrationEndDate,
                      registrationStartDate: item.registrationEndDate,
                      isClosed: item.isClosed,
                      isExpired: item.isExpired,
                      isComingSoon: item.isComingSoon,
                      price: item.price,
                      paymentKind: item.paymentKind,
                    }}
                    hasAccess={false}
                  />
                );
              }}
            />
          </SectionContainer>
        ) : null}
        <Box className={BundleSectionKind['InfoPanel']}>
          <InfoPanel1Section
            bundle={bundle}
            getStartedUrl={getStartedUrl}
            userAccessState={userAccessState}
          />
        </Box>
        {bundle?.liveBannerSection ? (
          <Box className={BundleSectionKind['LiveBannerSection']} py={40}>
            <LiveBannerSection content={bundle?.liveBannerSection} />
          </Box>
        ) : null}
        {bundle?.infoPanel2 ? (
          <Container fullWidth className={BundleSectionKind['InfoPanel_2']}>
            <InfoPanel2Section
              content={bundle.infoPanel2}
              fallbackImage={bundle.heroImage}
            />
          </Container>
        ) : null}
        {bundle?.infoPanel3 ? (
          <Box className={BundleSectionKind['InfoPanel_3']}>
            <InfoPanel3Section
              content={bundle.infoPanel3}
              fallbackImage={bundle.heroImage}
            />
          </Box>
        ) : null}
        {bundle?.twoColumnsSection ? (
          <div
            className={`${BundleSectionKind['TwoColumnsSection']} bg-neutral-50`}
          >
            <TextSection content={bundle.twoColumnsSection} />
          </div>
        ) : null}
        {bundle.promotionSection && !currentUser?.isPremium ? (
          <Box className={BundleSectionKind['PromotionSection']}>
            <PromotionSection
              content={bundle.promotionSection}
              promotionSubscription={bundle?.subscriptionPromotion}
              onPaymentComplete={() => {
                refetch().then(() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                });
              }}
            />
          </Box>
        ) : null}
        {bundle?.bundleItems?.programs?.items?.length > 0 ? (
          <SectionContainer
            waitListEnabled={isActiveWaitlist}
            className={[
              'Content__Section ',
              BundleSectionKind['Programs'],
            ].join(' ')}
            py={40}
            px={20}
          >
            <Section
              className="!p-0"
              titleAs="h2"
              title={
                bundle?.bundleItems?.programs?.headline || (
                  <Trans id="bundle.id_page.programs_sections.title" />
                )
              }
              viewAllUrl={
                isContentHubBundle && bundle?.bundleItems?.programs?.viewAllUrl
              }
              items={bundle?.bundleItems.programs?.items}
              initialItemsLimit={initialLimit}
              renderItem={(item: EssentialGenericFieldsFragment) => {
                if (bundle?.bundleItems.programs?.items?.length === 1) {
                  return (
                    <SingleProgramCard
                      key={item.slug}
                      program={item}
                      programCategory={item.category}
                    />
                  );
                } else {
                  return (
                    <ProgramCard
                      key={item.slug}
                      program={buildProgramStructor(item)}
                      programCategory={item.category}
                    />
                  );
                }
              }}
            />
          </SectionContainer>
        ) : null}
        {bundle?.bundleItems?.challenges?.items?.length > 0 ? (
          <SectionContainer
            waitListEnabled={isActiveWaitlist}
            className={[
              'Content__Section ',
              BundleSectionKind['Challenges'],
            ].join(' ')}
            py={40}
            px={20}
          >
            <Section
              className="!p-0"
              titleAs="h2"
              title={
                bundle?.bundleItems?.challenges?.headline || (
                  <Trans id="bundle.id_page.challenges_sections.title" />
                )
              }
              viewAllUrl={
                isContentHubBundle &&
                bundle?.bundleItems?.challenges?.viewAllUrl
              }
              items={bundle?.bundleItems.challenges?.items}
              initialItemsLimit={initialLimit}
              renderItem={(item: EssentialGenericFieldsFragment) => {
                if (bundle?.bundleItems.challenges?.items?.length === 1) {
                  return (
                    <SingleProgramCard
                      key={item.slug}
                      program={item}
                      href={`/${lang}/programs/${item.slug}/overview`}
                      programCategory={ProgramCategory['Challenges']}
                    />
                  );
                } else {
                  return (
                    <ProgramCard
                      key={item.slug}
                      program={buildProgramStructor(item)}
                      programCategory={item.category}
                    />
                  );
                }
              }}
            />
          </SectionContainer>
        ) : null}
        {bundle?.bundleItems?.blogPosts?.items?.length > 0 ? (
          <SectionContainer
            waitListEnabled={isActiveWaitlist}
            className={[
              'Content__Section ',
              BundleSectionKind['BlogPosts'],
            ].join(' ')}
            py={40}
            px={20}
            fullWidth
          >
            <Section
              className="!p-0"
              titleAs="h2"
              title={
                bundle?.bundleItems?.blogPosts?.headline || (
                  <Trans id="bundle.id_page.blog_sections.title" />
                )
              }
              viewAllUrl={
                isContentHubBundle && bundle?.bundleItems?.blogPosts?.viewAllUrl
              }
              items={bundle?.bundleItems.blogPosts?.items}
              initialItemsLimit={initialLimit}
              renderItem={(item: BlogPostFieldsFragment) => {
                return (
                  <BlogCard
                    key={item.id}
                    blog={{
                      id: item.id,
                      title: item.title,
                      slug: item.slug,
                      featuredImageUrl: item.featuredImage,
                    }}
                  />
                );
              }}
            />
          </SectionContainer>
        ) : null}
        {bundle?.bundleItems?.documents?.items?.length > 0 ? (
          <SectionContainer
            waitListEnabled={isActiveWaitlist}
            className={[
              'Content__Section ',
              BundleSectionKind['Documents'],
            ].join(' ')}
            py={40}
            px={20}
            fullWidth
          >
            <Section
              className="!p-0"
              titleAs="h2"
              title={
                bundle?.bundleItems?.documents?.headline || (
                  <Trans id="bundle.id_page.documents_sections.title" />
                )
              }
              viewAllUrl={
                isContentHubBundle && bundle?.bundleItems?.documents?.viewAllUrl
              }
              items={bundle?.bundleItems.documents?.items}
              initialItemsLimit={6}
              renderItem={(item: Document) => {
                return (
                  <CardLink
                    key={item.name}
                    {...(userAccessState === BundleAccessState.Active
                      ? { href: item.url, target: '_blank' }
                      : { onClick: handleDocumentClick })}
                  >
                    <DocumentCard title={item.name} />
                  </CardLink>
                );
              }}
            />
          </SectionContainer>
        ) : null}
        {userAccessState === BundleAccessState.Active &&
        bundle?.footerCta?.title ? (
          <Box className="ExtraSection " py={40}>
            <BundleExtraSection bundle={bundle} />
          </Box>
        ) : null}
        {bundle.relatedBundlesSection?.bundles?.length > 0 ? (
          <SectionContainer
            className={[
              'Content__Section ',
              BundleSectionKind['RelatedBundles'],
            ].join(' ')}
            py={40}
            px={20}
            fullWidth
          >
            <Container>
              <Section
                className="!p-0"
                titleAs="h2"
                title={
                  bundle.relatedBundlesSection?.headline || (
                    <Trans
                      id="bundle.id_page.related_bundles_sections.title"
                      message="Related bundles"
                    />
                  )
                }
                items={bundle.relatedBundlesSection?.bundles}
                renderItem={(item) => (
                  <BundleCard key={item?.id} bundle={item} />
                )}
                viewAllUrl={`/${lang}/needs-and-themes`}
                initialItemsLimit={6}
              />
            </Container>
          </SectionContainer>
        ) : null}
        {!bundle && loading ? (
          <Loading src="/static/icons/loading-dots.svg" />
        ) : null}
        {(!currentUser?.id || !currentUser?.isPremium) &&
        bundle?.bundleCategory === BundleCategoryKind['ContentHub'] ? (
          <SectionContainer className="Pricing">
            <PricingSection promotionCode={bundle?.specialOfferCode} />
          </SectionContainer>
        ) : null}
      </Content>
    </div>
  );
};

export async function getServerSideProps(ctx) {
  const { slug, client, preview } = ctx.query;

  try {
    const apolloClient = ctx.req.apolloClient;
    const { data } = await apolloClient.query({
      query: BundleDocument,
      variables: { slug, client, previewParameter: preview },
    });

    if (!data?.bundle) {
      return {
        props: {},
      };
    }

    return { props: { ssrData: data } };
  } catch (ex) {
    console.error('bundle getServerSideProps', ex);
    return {
      props: {},
    };
  }
}

export default BundlePage;
